import React, { useState, useEffect } from 'react';
import { MessageSquare, Check, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminMessages = () => {
  const { token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/admin/contact`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, [token]);

  const markAsRead = async (messageId) => {
    try {
      await axios.put(`${API}/admin/contact/${messageId}/read`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchMessages();
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  return (
    <div data-testid="admin-messages" className="p-6 lg:p-10">
      <div className="mb-10">
        <h1 className="text-3xl font-bold tracking-tight">
          Contact Messages
        </h1>
        <p className="mt-2 text-neutral-500">
          {messages.filter(m => !m.read).length} unread message{messages.filter(m => !m.read).length !== 1 ? 's' : ''}
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="spinner" />
        </div>
      ) : messages.length === 0 ? (
        <div className="text-center py-20">
          <MessageSquare size={48} className="mx-auto text-neutral-300 mb-4" />
          <p className="text-neutral-500">No messages yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {messages.map((message, index) => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`bg-white border p-6 ${
                message.read ? 'border-neutral-200' : 'border-black'
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {!message.read && (
                      <span className="w-2 h-2 bg-black rounded-full" />
                    )}
                    <h3 className="font-semibold">{message.subject}</h3>
                  </div>
                  <div className="flex items-center gap-4 text-neutral-400 text-sm mb-4">
                    <span>{message.name}</span>
                    <span>{message.email}</span>
                    <div className="flex items-center gap-1">
                      <Calendar size={12} />
                      <span>{new Date(message.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <p className="text-neutral-600 leading-relaxed">{message.message}</p>
                </div>

                {!message.read && (
                  <button
                    onClick={() => markAsRead(message.id)}
                    data-testid={`mark-read-${message.id}`}
                    className="p-2 border border-neutral-200 hover:border-black transition-colors"
                    title="Mark as read"
                  >
                    <Check size={18} />
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminMessages;
